import {Injectable, TemplateRef, Type} from '@angular/core';
import {unblockBodyScroll} from 'primeng/dom';
import {BehaviorSubject} from 'rxjs';
import {AppDialogConfig} from './app-dialog.service';
import {AppDialogRef} from './app-dialog-ref';
import {DrawerEntry} from './app-drawer.types';
import {
	APP_DRAWER_BASE_Z_INDEX,
	APP_DRAWER_CLOSE_ANIMATION_MS,
	buildDrawerStyleClass,
} from './drawer.defaults';

@Injectable({providedIn: 'root'})
export class AppDrawerService {
	private nextId = 0;
	private entries: DrawerEntry[] = [];
	private readonly entriesSubject = new BehaviorSubject<DrawerEntry[]>([]);
	private readonly finalizeTimers = new Map<number, ReturnType<typeof setTimeout>>();
	readonly entries$ = this.entriesSubject.asObservable();

	open<T>(content: Type<T> | TemplateRef<unknown>, config: AppDialogConfig = {}): AppDialogRef {
		const appRef = new AppDialogRef();
		const closable = config.closable !== false;
		const isTemplate = content instanceof TemplateRef;
		const id = ++this.nextId;
		const entry: DrawerEntry = {
			id,
			visible: false,
			closing: false,
			styleClass: buildDrawerStyleClass(config.dialogClass),
			width: config.width,
			kind: isTemplate ? 'template' : 'component',
			content: content as TemplateRef<unknown> | Type<unknown>,
			context: config.context,
			ref: appRef,
			closable,
			showCloseButton: config.shellCloseButton ?? isTemplate,
			// Fermeture au clic extérieur : indépendante du bouton X (closable).
			dismissible: config.closeOnBackdropClick !== false,
			closeOnEscape: config.closeOnEsc ?? closable,
			modal: config.hasBackdrop !== false,
			header: config.header,
			showHeader: config.showHeader ?? !isTemplate,
			stickyChrome: config.stickyChrome === true,
			zIndex: APP_DRAWER_BASE_Z_INDEX + this.entries.length * 10,
		};

		appRef.attachCloseHandler((result) => this.requestClose(id, result));
		this.entries = [...this.entries, entry];
		this.publish();

		setTimeout(() => {
			const current = this.entries.find((item) => item.id === id);
			if (!current || current.closing) {
				return;
			}
			current.visible = true;
			this.publish();
		}, 0);

		return appRef;
	}

	requestClose(id: number, result?: unknown): void {
		const entry = this.entries.find((item) => item.id === id);
		if (!entry || entry.closing) {
			return;
		}

		entry.closing = true;
		entry.closeResult = result;
		entry.visible = false;
		this.publish();
		this.scheduleFinalize(id);
	}

	scheduleFinalize(id: number): void {
		if (this.finalizeTimers.has(id)) {
			return;
		}

		const timer = setTimeout(() => {
			this.finalizeTimers.delete(id);
			this.finalizeClose(id);
		}, APP_DRAWER_CLOSE_ANIMATION_MS);
		this.finalizeTimers.set(id, timer);
	}

	getSnapshot(): DrawerEntry[] {
		return this.entries;
	}

	hasOpenDrawer(): boolean {
		return this.entries.some((entry) => entry.visible && !entry.closing);
	}

	private finalizeClose(id: number): void {
		const entry = this.entries.find((item) => item.id === id);
		if (!entry) {
			return;
		}

		const result = entry.closeResult;
		this.entries = this.entries.filter((item) => item.id !== id);
		this.publish();
		this.cleanupOrphanDrawerOverlays();
		entry.ref.notifyClosed(result);
	}

	private cleanupOrphanDrawerOverlays(): void {
		const hasOpenDynamicDrawer = this.entries.some((entry) => entry.visible && !entry.closing);
		const hasOpenDrawerInDom = !!document.querySelector('.p-drawer[data-p-open="true"]');
		if (hasOpenDynamicDrawer || hasOpenDrawerInDom) {
			return;
		}

		document.querySelectorAll('.p-drawer-mask, .p-drawer-mask-scrollblocker').forEach((mask) => {
			mask.remove();
		});
		unblockBodyScroll();
	}

	private publish(): void {
		this.entriesSubject.next([...this.entries]);
	}
}
